import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const BUILDING_TYPES = [
  { id: "kontor", air: 2.5, heat: 40, cool: 45 },
  { id: "bolig", air: 1.2, heat: 35, cool: 25 },
  { id: "skole", air: 3.0, heat: 50, cool: 40 },
  { id: "industri", air: 4.0, heat: 60, cool: 80 },
];

const LOCALES = { no: "nb-NO", en: "en-GB", pl: "pl-PL" };

const Estimator = () => {
  const [typeId, setTypeId] = useState("kontor");
  const [area, setArea] = useState(2000);
  const { t, lang } = useLanguage();

  const fmt = (n) => new Intl.NumberFormat(LOCALES[lang] || "nb-NO", { maximumFractionDigits: 0 }).format(n);

  const result = useMemo(() => {
    const type = BUILDING_TYPES.find((b) => b.id === typeId);
    const a = Math.max(0, Number(area) || 0);
    return {
      air: a * type.air * 3.6,
      heat: (a * type.heat) / 1000,
      cool: (a * type.cool) / 1000,
    };
  }, [typeId, area]);

  return (
    <section className="blueprint-grid border-t border-slate-200 bg-white py-20 sm:py-28" data-testid="estimator-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="estimator-badge">
              {t("estimator.badge")}
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="estimator-title">
              {t("estimator.title")}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {t("estimator.intro")}
            </p>
            <div className="mt-8 space-y-6">
              <div>
                <label htmlFor="est-type" className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {t("estimator.typeLabel")}
                </label>
                <select
                  id="est-type"
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                  data-testid="estimator-type-select"
                  className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 transition-colors duration-200 focus:border-sky-600 focus:outline-none"
                >
                  {BUILDING_TYPES.map((b) => (
                    <option key={b.id} value={b.id}>{t(`estimator.types.${b.id}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="est-area" className="font-mono text-xs uppercase tracking-widest text-slate-500">
                  {t("estimator.areaLabel")}
                </label>
                <input
                  id="est-area"
                  type="number"
                  min="0"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  data-testid="estimator-area-input"
                  className="mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 transition-colors duration-200 focus:border-sky-600 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="corner-frame rounded-lg border border-slate-200 bg-white p-8 shadow-md sm:p-10" data-testid="estimator-results">
            <div className="flex items-center gap-3">
              <Calculator size={20} className="text-sky-700" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">{t("estimator.resultsLabel")}</span>
            </div>
            <div className="mt-8 space-y-7">
              <div data-testid="estimator-result-air">
                <div className="font-display text-3xl font-bold text-sky-700 sm:text-4xl">
                  {fmt(result.air)} <span className="text-lg font-medium text-slate-500">m³/h</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{t("estimator.airLabel")}</p>
              </div>
              <div className="border-t border-slate-100 pt-7" data-testid="estimator-result-heat">
                <div className="font-display text-3xl font-bold text-orange-600 sm:text-4xl">
                  {fmt(result.heat)} <span className="text-lg font-medium text-slate-500">kW</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{t("estimator.heatLabel")}</p>
              </div>
              <div className="border-t border-slate-100 pt-7" data-testid="estimator-result-cool">
                <div className="font-display text-3xl font-bold text-sky-500 sm:text-4xl">
                  {fmt(result.cool)} <span className="text-lg font-medium text-slate-500">kW</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{t("estimator.coolLabel")}</p>
              </div>
            </div>
            <a
              href="#kontakt"
              data-testid="estimator-cta"
              className="mt-9 block rounded-md bg-sky-600 px-6 py-3.5 text-center text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700"
            >
              {t("estimator.cta")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Estimator;
